// src/pages/MaterialsPage.js
import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Accordion, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import BackgroundLayout from '../components/BackgroundLayout';
import logo from '../assets/p25-200.png';

function MaterialsPage() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  // State for managing accordions - initially all closed
  const [activeKey, setActiveKey] = useState(null);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch materials from JSON file
  useEffect(() => {
    const fetchMaterials = async () => {
      // Don't fetch if user is not authenticated
      if (!isAuthenticated) return;

      try {
        // Fetch materials from the JSON file
        const response = await fetch('/materials.json');

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const fetchedMaterials = data.materials || [];
        setMaterials(fetchedMaterials);

        // Set first module as active if there are any
        if (fetchedMaterials.length > 0) {
          setActiveKey(fetchedMaterials[0].id);
        }
      } catch (err) {
        console.error('Error fetching materials:', err);
        setError('Nie udało się pobrać materiałów. Spróbuj ponownie później.');
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, [isAuthenticated]);

  // Format file size for display
  const formatFileSize = (size) => {
    return size;
  };

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    switch(fileType) {
      case 'pdf':
        return <i className="bi bi-file-earmark-pdf-fill text-danger me-2"></i>;
      case 'mp4':
        return <i className="bi bi-file-earmark-play-fill text-primary me-2"></i>;
      case 'link':
        return <i className="bi bi-link-45deg text-success me-2"></i>;
      default:
        return <i className="bi bi-file-earmark me-2"></i>;
    }
  };

  // Handle file download
  const handleDownload = (filePath, fileName) => {
    try {
      // Reset any previous error
      setDownloadError(null);

      // Create a direct link to the file
      const link = document.createElement('a');
      link.href = filePath;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      setDownloadError(`Nie udało się pobrać pliku ${fileName}. Spróbuj ponownie później.`);

      // Clear error after 5 seconds
      setTimeout(() => {
        setDownloadError(null);
      }, 5000);
    }
  };

  // Handle external link opening
  const handleOpenLink = (url) => {
    try {
      // Reset any previous error
      setDownloadError(null);

      // Open the URL in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening link:', err);
      setDownloadError(`Nie udało się otworzyć linku. Spróbuj ponownie później.`);

      // Clear error after 5 seconds
      setTimeout(() => {
        setDownloadError(null);
      }, 5000);
    }
  };

  // Count total files and links across all modules
  const getTotalItemCount = () => {
    return materials.reduce((total, module) => total + module.files.length, 0);
  };

  // Get count of specific item types
  const getTypeCount = (type) => {
    return materials.reduce((count, module) => {
      return count + module.files.filter(file => file.type === type).length;
    }, 0);
  };

  // If user is not authenticated, don't display content
  if (!isAuthenticated) {
    return null; // Redirect will happen in useEffect
  }

  // Display loading spinner
  if (loading) {
    return (
      <BackgroundLayout>
        <div className="text-center py-5 bg-light bg-opacity-75 rounded shadow">
          <Spinner animation="border" variant="secondary" />
          <p className="mt-3">Ładowanie materiałów szkoleniowych...</p>
        </div>
      </BackgroundLayout>
    );
  }

  // Display error
  if (error) {
    return (
      <BackgroundLayout>
        <Alert variant="danger">
          <Alert.Heading>Wystąpił błąd!</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Odśwież stronę
          </Button>
        </Alert>
      </BackgroundLayout>
    );
  }

  return (
    <BackgroundLayout>
      <Card className="bg-light bg-opacity-75 rounded shadow p-3">
        <Card.Body>
          <Row className="align-items-center mb-4">
            <Col>
              <h1 className="m-0">
                Materiały
                {materials.length > 0 && (
                  <span className="badge bg-secondary ms-2">
                    {getTotalItemCount()} elementów
                  </span>
                )}
              </h1>
              {materials.length > 0 && (
                <div className="mt-2">
                  {getTypeCount('pdf') > 0 && (
                    <Badge bg="danger" className="me-2">
                      <i className="bi bi-file-earmark-pdf-fill me-1"></i>
                      {getTypeCount('pdf')} PDF
                    </Badge>
                  )}
                  {getTypeCount('mp4') > 0 && (
                    <Badge bg="primary" className="me-2">
                      <i className="bi bi-file-earmark-play-fill me-1"></i>
                      {getTypeCount('mp4')} wideo
                    </Badge>
                  )}
                  {getTypeCount('link') > 0 && (
                    <Badge bg="success" className="me-2">
                      <i className="bi bi-link-45deg me-1"></i>
                      {getTypeCount('link')} linków
                    </Badge>
                  )}
                </div>
              )}
            </Col>
            <Col xs="auto">
              <img src={logo} alt="logo" className="img-fluid mb-2" style={{ maxHeight: '80px' }} />
            </Col>
          </Row>

          {downloadError && (
            <Alert variant="warning" className="mb-4" dismissible onClose={() => setDownloadError(null)}>
              <p>{downloadError}</p>
            </Alert>
          )}

          {materials.length === 0 ? (
            <Alert variant="info">
              Brak dostępnych materiałów.
            </Alert>
          ) : (
            <Accordion activeKey={activeKey} onSelect={(eventKey) => setActiveKey(eventKey)}>
              {materials.map((module, moduleIndex) => (
                <div key={module.id} className={moduleIndex > 0 ? "mt-3" : ""}>
                  <Accordion.Item eventKey={module.id}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <div>
                          <span className="h4 mb-0">{module.title}</span>
                          <span className="badge bg-secondary ms-2">
                            {module.files.length} {module.files.length === 1 ? 'element' : 'elementów'}
                          </span>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {module.description && (
                        <Row className="mt-2 mb-3">
                          <Col xs={12}>
                            <p>{module.description}</p>
                          </Col>
                        </Row>
                      )}

                      <div className="mt-3">
                        <h5>Dostępne materiały:</h5>
                        <div className="list-group">
                          {module.files.map((item) => (
                            <div key={item.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center flex-grow-1">
                                {getFileIcon(item.type)}
                                <div>
                                  <div className="fw-bold">{item.name}</div>
                                  {item.type === 'link' && item.description && (
                                    <small className="text-muted">{item.description}</small>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex align-items-center ms-2">
                                {item.type !== 'link' && (
                                  <small className="text-muted me-3">{formatFileSize(item.size)}</small>
                                )}

                                {item.type === 'link' ? (
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleOpenLink(item.url)}
                                  >
                                    <i className="bi bi-box-arrow-up-right me-1"></i>
                                    Otwórz
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleDownload(item.path, item.name)}
                                  >
                                    <i className="bi bi-download me-1"></i>
                                    Pobierz
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </div>
              ))}
            </Accordion>
          )}
        </Card.Body>
      </Card>
    </BackgroundLayout>
  );
}

export default MaterialsPage;
